import { all, call, put, takeLatest, takeLeading } from 'redux-saga/effects';
import { aiqEventTriggersConfigurationActions } from './aiqEventTriggersConfigurationSlice';
import { fetchAllLocations } from 'services/userLocationService';
import notificationsHandler from 'utils/notificationsHandler';
import { parseErrorMessageFromResponse } from 'utils/formatErrorCode';

import {
    fetchAiqEventTriggersConfigurationCameras,
    fetchLocationMonitoringObjects,
    fetchMonitoringObjectCameras,
    updateAiqEventTriggersConfiguration,
    postAiqEventTriggersConfiguration,
} from 'services/monitoringService';
import { CONFIGURATION_DIALOG_MODE, GET_ALL_CAMERAS_TYPE } from '../constants';

function* httpRequest(request, ...args) {
    try {
        yield put(aiqEventTriggersConfigurationActions.requestStart(''));
        const data = yield call(request, ...args);
        yield put(aiqEventTriggersConfigurationActions.requestSucces());
        return data;
    } catch (err) {
        yield put(aiqEventTriggersConfigurationActions.requestError());
        throw err;
    }
}

function* getLocationMonitoringObjects({ payload: locationId }) {
    try {
        const data = yield call(httpRequest, fetchLocationMonitoringObjects, locationId, true);
        yield put(
            aiqEventTriggersConfigurationActions.setMonitoringObjects(
                data.map((mo) => {
                    mo.title = createLabel(mo);
                    return mo;
                })
            )
        );
    } catch (err) {
        yield call(notificationsHandler, { err, title: `Error fetching monitoring objects for location id=${locationId}` });
    }
}

function* getAllLocations() {
    try {
        const data = yield call(httpRequest, fetchAllLocations);
        const sortLocations = (a, b) => (a.yardName?.toLowerCase?.() > b.yardName?.toLowerCase?.() ? 1 : -1);
        data.sort(sortLocations);
        yield put(aiqEventTriggersConfigurationActions.setAllLocations(data));
    } catch (err) {
        yield call(notificationsHandler, { err, title: 'Error fetching all locations' });
    }
}

function* getConfiguredCameras() {
    try {
        yield put(aiqEventTriggersConfigurationActions.requestStart(GET_ALL_CAMERAS_TYPE));
        const data = yield call(fetchAiqEventTriggersConfigurationCameras);
        const formatedData = data.map((record) => {
            const mo = record.monitoringObjects?.[0];
            if (mo) mo.label = createLabel(mo);
            return record;
        });
        yield put(aiqEventTriggersConfigurationActions.setConfiguredCameras(formatedData));
        yield put(aiqEventTriggersConfigurationActions.requestSucces());
    } catch (err) {
        yield call(notificationsHandler, { err, title: 'Error fetching audio configured cameras' });
        yield put(aiqEventTriggersConfigurationActions.requestError());
    }
}

function* getInitialData() {
    yield call(getConfiguredCameras);
    yield all([call(getAllLocations)]);
}

function* getMonitoringObjectCameras({ payload: monitoringObjectId }) {
    try {
        const data = yield call(httpRequest, fetchMonitoringObjectCameras, monitoringObjectId);
        yield put(aiqEventTriggersConfigurationActions.setMonitoringObjectCameras(data));
    } catch (err) {
        yield call(notificationsHandler, { err, title: 'Error fetching cameras for selected monitoring object' });
    }
}

function* submitChanges({ payload: { selectedMonitoringObject, selectedCamera, selectedWebHookType, isWebHookActive, extensionNumber } }) {
    let isFormValid = true;
    const monitoringObject = selectedMonitoringObject;
    const id = selectedCamera?.id;

    if (!id || !monitoringObject.id || !extensionNumber) isFormValid = false;

    if (!isFormValid) {
        yield call(notificationsHandler, { title: 'Please review your inputs', variant: 'info' });
        return;
    }

    const data = { payload: { id, extensionNumber, monitoringObject, selectedWebHookType, isWebHookActive } };
    yield call(saveCameraConfiguration, data);
}

function* generate({ payload: { selectedMonitoringObject, selectedCamera, selectedWebHookType, isWebHookActive } }) {
    let isFormValid = true;
    const monitoringObject = selectedMonitoringObject;
    const id = selectedCamera?.id;

    if (!id || !monitoringObject) isFormValid = false;

    if (!isFormValid) {
        yield call(notificationsHandler, { title: 'Please review your inputs', variant: 'info' });
        return;
    }

    const data = yield call(postAiqEventTriggersConfiguration, {
        id,
        ...(monitoringObject && { monitoringObject: { id: monitoringObject.id } }),
        sourceType: selectedWebHookType,
        activeWebhook: isWebHookActive,
    });

    yield put(aiqEventTriggersConfigurationActions.setExtensionNumber(data));
}

function* saveCameraConfiguration({ payload: { id, monitoringObject, extensionNumber, selectedWebHookType, isWebHookActive } }) {
    try {
        yield call(httpRequest, updateAiqEventTriggersConfiguration, {
            id,
            externalId: extensionNumber,
            ...(monitoringObject && { monitoringObject: { id: monitoringObject.id } }),
            sourceType: selectedWebHookType,
            activeWebhook: isWebHookActive,
        });

        yield call(getConfiguredCameras);
        yield put(aiqEventTriggersConfigurationActions.filterCameras());
        yield put(aiqEventTriggersConfigurationActions.setDialog({ open: false }));
        yield put(aiqEventTriggersConfigurationActions.setErrorMessage(''));
        const successMessage = `Camera audio type removed`;
        yield call(notificationsHandler, { title: successMessage, variant: 'success' });
    } catch (err) {
        const { error } = err.response.data;
        const errorMessage = parseErrorMessageFromResponse(error);
        yield call(notificationsHandler, { err, title: errorMessage });
        yield put(aiqEventTriggersConfigurationActions.setErrorMessage(errorMessage));
    }
}

function* openExistingRecord({ payload: record }) {
    yield put(aiqEventTriggersConfigurationActions.setDialog({ open: true, mode: CONFIGURATION_DIALOG_MODE.EDIT }));
    yield put(aiqEventTriggersConfigurationActions.setSelectedLocation({ yardName: record?.yardName }));
    if (record?.monitoringObject) yield put(aiqEventTriggersConfigurationActions.setSelectedMonitoringObject(record?.monitoringObject));
    yield put(aiqEventTriggersConfigurationActions.setSelectedWebHookType(record?.sourceType));
    yield put(aiqEventTriggersConfigurationActions.toggleWebHookActive(record?.activeWebhook));
    yield put(aiqEventTriggersConfigurationActions.setSelectedCamera({ name: record?.name, id: record?.id }));
    yield put(aiqEventTriggersConfigurationActions.setExtensionNumber(record));
}

function* onGetLocationMonitoringObjects() {
    yield takeLatest(aiqEventTriggersConfigurationActions.getMonitoringObjects, getLocationMonitoringObjects);
}

function* onGetAllLocations() {
    yield takeLatest(aiqEventTriggersConfigurationActions.getAllLocations, getAllLocations);
}

function* onGetConfiguredCameras() {
    yield takeLatest(aiqEventTriggersConfigurationActions.getConfiguredCameras, getConfiguredCameras);
}

function* onGetInitialData() {
    yield takeLatest(aiqEventTriggersConfigurationActions.getInitialData, getInitialData);
}

function* onGetMonitoringObjectCameras() {
    yield takeLatest(aiqEventTriggersConfigurationActions.getMonitoringObjectCameras, getMonitoringObjectCameras);
}

function* onSubmitChanges() {
    yield takeLatest(aiqEventTriggersConfigurationActions.submitChanges, submitChanges);
}

function* onGenerate() {
    yield takeLatest(aiqEventTriggersConfigurationActions.generateHandler, generate);
}

function* onOpenExistingRecord() {
    yield takeLatest(aiqEventTriggersConfigurationActions.openExistingRecord, openExistingRecord);
}

function* onSaveCameraConfiguration() {
    yield takeLeading(aiqEventTriggersConfigurationActions.saveCameraConfiguration, saveCameraConfiguration);
}
export function* aiqEventTriggersConfigurationSaga() {
    yield all([
        call(onGetAllLocations),
        call(onGetLocationMonitoringObjects),
        call(onGetConfiguredCameras),
        call(onGetInitialData),
        call(onGetMonitoringObjectCameras),
        call(onSubmitChanges),
        call(onGenerate),
        call(onOpenExistingRecord),
        call(onSaveCameraConfiguration),
    ]);
}

const createLabel = (mo) => `${mo.name} (${mo.objectType})`;
